import {
  ViewButton,
  EditButton,
  PersonButton,
  HistoryButton,
  ForceToChiedimiCCButton,
  ReprocessLeadButton,
  AiConversationsButton,
} from '../Common/DataTable/tableActionButtons';
import {
  getFilter,
  getLabel,
  getSort,
  hideSensitiveData,
  qualifiedName,
} from '../Common/DataTable/tableHelpers';
import moment from 'moment';
import FromNow from '../Common/FromNow';
import {
  CONVERSATION_STATUS,
  conversationClosure,
} from '../../config/aiConversationStatus';
import callOutcomes from '../../config/callOutcomes';

const NO_EDIT_PERSON_ID_STATUS = ['new', 'notAllowed', 'error'];

const statusName = (row) => {
  if (row.qualified === 'new' || row.survey === 'new') return 'new';
  else if (row.error === '' || !row.error) return 'ok';
  return 'error';
};

const createTableColumns = (
  openViewModal,
  openEditModal,
  openPersonModal,
  openForceToChiedimiCCAlert,
  openReprocessLeadAlert,
  openHistoryModal,
  campaigns,
  showSensitiveData,
  openAiConversationsModal,
  // { byLead, showCost, t }, or null for a caller with no AI data: the query
  // test table passes eight arguments and gets no AI column. NEVER merge
  // byLead into the rows — updateLeadSaga writes on Firestore whatever a row
  // carries.
  ai = null,
) => {
  const actions = openEditModal
    ? {
        title: 'Action',
        name: 'action',
        width: 200,
        align: 'center',
        formatterComponent: ({ row }) => (
          <ul className="list-inline font-size-20 contact-links mb-0">
            <ViewButton row={row} onPress={openViewModal} />
            <EditButton row={row} onPress={openEditModal} />
            {openPersonModal &&
              !NO_EDIT_PERSON_ID_STATUS.includes(row.personId) && (
                <PersonButton row={row} onPress={openPersonModal} />
              )}
            {openForceToChiedimiCCAlert &&
              row.qualified === 'qualificationRefused' &&
              !row.sentToChiedimiCC &&
              (row.error === '' || !row.error) && (
                <ForceToChiedimiCCButton
                  row={row}
                  onPress={openForceToChiedimiCCAlert}
                />
              )}
            {openReprocessLeadAlert &&
              (row.qualified === 'error' ||
                row.survey === 'error' ||
                row.personId === 'error') && (
                <ReprocessLeadButton
                  row={row}
                  onPress={openReprocessLeadAlert}
                />
              )}
            {row.qualified !== 'new' && openHistoryModal && (
              <HistoryButton row={row} onPress={openHistoryModal} />
            )}
            {/*
              aiConversationId is the only mark a lead keeps of having been
              through the agent, and it keeps it for good: the icon says
              there is something to read, the modal says whether it is live.
            */}
            {row.aiConversationId && openAiConversationsModal && (
              <AiConversationsButton
                row={row}
                onPress={openAiConversationsModal}
              />
            )}
          </ul>
        ),
        filter: 'disabled',
        sorting: 'disabled',
      }
    : {
        title: 'Action',
        name: 'action',
        hidden: true,
        filter: 'disabled',
        sorting: 'disabled',
      };

  // ai.byLead can be undefined: redux-persist's autoMergeLevel1 replaces the
  // whole Leads slice with whatever a prior build persisted, and a build
  // before this field existed persisted no aiByLead key at all. A returning
  // user's first render after this release must not throw on that.
  const conversationOf = (row) => (ai && (ai.byLead || {})[row.id]) || null;
  const translate = (ai && ai.t) || ((key) => key);
  const when = (date) => (date ? moment(date).format('DD/MM/YY HH:mm') : '');

  const badge = (color, value) =>
    value ? (
      <span className={`badge badge-${color} font-size-12 text-wrap`}>
        {value}
      </span>
    ) : (
      ''
    );

  const aiColumns = !ai
    ? []
    : [
        {
          title: 'Handled by AI?',
          name: 'ai',
          width: 110,
          // From the lead's own field, so it answers even when the
          // conversation started outside the range the map was read for.
          // The title asks and the cell answers: a column headed AI whose
          // cell also read AI said the same word twice and neither said
          // what it meant.
          getCellValue: (row) => (row.aiConversationId ? 'yes' : 'no'),
          formatterComponent: ({ value }) =>
            value === 'yes' ? badge('info', 'YES') : '',
        },
        {
          title: 'AI Status',
          name: 'aiStatus',
          width: 150,
          getCellValue: (row) => {
            const conversation = conversationOf(row);
            if (!conversation) return '';
            const status = CONVERSATION_STATUS[conversation.status];
            return translate(status ? status.title : conversation.status);
          },
          formatterComponent: ({ value, row }) => {
            const conversation = conversationOf(row);
            const status =
              conversation && CONVERSATION_STATUS[conversation.status];
            return badge(status ? status.color : 'light', value);
          },
        },
        {
          title: 'AI Closure',
          name: 'aiClosure',
          width: 180,
          getCellValue: (row) => {
            const { title } = conversationClosure(conversationOf(row));
            return title ? translate(title) : '';
          },
          formatterComponent: ({ value, row }) =>
            badge(conversationClosure(conversationOf(row)).color, value),
        },
        {
          title: 'AI Outcome',
          name: 'aiOutcome',
          width: 150,
          // Hidden: an outcome is written only by a real closure, so this
          // column repeats AI Closure whenever it says anything and is empty
          // whenever AI Closure is the informative one. It stays available
          // because filtering on the outcome alone is easier here than on
          // AI Closure, which mixes in the nine handoff reasons.
          hidden: true,
          getCellValue: (row) => {
            const conversation = conversationOf(row);
            const outcome = conversation && callOutcomes[conversation.outcome];
            return outcome ? translate(outcome.title) : '';
          },
          formatterComponent: ({ value, row }) => {
            const conversation = conversationOf(row);
            const outcome = conversation && callOutcomes[conversation.outcome];
            return outcome ? (
              <span
                className={`badge badge-${outcome.color} font-size-12 text-wrap`}
              >
                <i className={`${outcome.icon} align-middle mr-1`}></i>
                {value}
              </span>
            ) : (
              ''
            );
          },
        },
        {
          title: 'AI Reason',
          name: 'aiReason',
          width: 170,
          // The positive causale or the negative reason, as the tool wrote it.
          getCellValue: (row) =>
            ((conversationOf(row) || {}).outcomeDetail || {}).reason || '',
          hidden: true,
        },
        {
          title: 'Turns',
          name: 'aiTurnCount',
          width: 90,
          getCellValue: (row) => {
            const conversation = conversationOf(row);
            if (!conversation) return '';
            return `${conversation.turnCount || 0} / ${
              (conversation.agentConfig || {}).maxTurns || '-'
            }`;
          },
          filter: 'disabled',
          // The cell carries formatted text ("7 / 20", "$ 0.0321", a
          // DD/MM/YY HH:mm string), which the grid's default comparator
          // would order lexicographically rather than by the real value.
          sorting: 'disabled',
          hidden: true,
        },
        {
          title: 'AI Agent',
          name: 'aiAgentName',
          width: 140,
          // From the snapshot the conversation carries, not from the agent as
          // it is configured today: it is what spoke to this lead.
          getCellValue: (row) => {
            const conversation = conversationOf(row);
            if (!conversation) return '';
            return (
              (conversation.agentConfig || {}).name ||
              conversation.agentId ||
              ''
            );
          },
          hidden: true,
        },
        {
          title: 'AI Started',
          name: 'aiStartedAt',
          width: 130,
          getCellValue: (row) => when((conversationOf(row) || {}).startedAt),
          filter: 'disabled',
          sorting: 'disabled',
          hidden: true,
        },
        {
          title: 'AI Ended',
          name: 'aiEndedAt',
          width: 130,
          getCellValue: (row) => when((conversationOf(row) || {}).endedAt),
          filter: 'disabled',
          sorting: 'disabled',
          hidden: true,
        },
        ...(ai.showCost
          ? [
              {
                title: 'AI Cost',
                name: 'aiCost',
                width: 110,
                // llmUsd carries its unit in its name: the engine is billed in
                // dollars and storing a conversion rate was refused.
                getCellValue: (row) => {
                  const { llmUsd } = (conversationOf(row) || {}).cost || {};
                  return llmUsd ? `$ ${llmUsd.toFixed(4)}` : '';
                },
                filter: 'disabled',
                sorting: 'disabled',
                hidden: true,
              },
            ]
          : []),
      ];

  return [
    {
      title: 'Campaign',
      name: 'campaignId',
      formatterComponent: ({ value }) => getLabel(value, campaigns),
      sorting: (a, b) =>
        getSort(getLabel(a, campaigns), getLabel(b, campaigns)),
      filter: (value, filter) =>
        getFilter(getLabel(value, campaigns), filter.value),
      hidden: true,
    },
    {
      title: 'Name',
      name: 'name',
      formatterComponent: ({ value }) =>
        showSensitiveData ? value : hideSensitiveData(value, 4),
      filter: showSensitiveData ? undefined : 'disabled',
      sorting: showSensitiveData ? undefined : 'disabled',
    },
    {
      title: 'Email',
      name: 'email',
      formatterComponent: ({ value }) =>
        showSensitiveData ? value : hideSensitiveData(value, 4),
      filter: showSensitiveData ? undefined : 'disabled',
      sorting: showSensitiveData ? undefined : 'disabled',
    },
    {
      title: 'Phone',
      name: 'phone',
      formatterComponent: ({ value }) =>
        showSensitiveData ? value : hideSensitiveData(value, 6),
      filter: showSensitiveData ? undefined : 'disabled',
      sorting: showSensitiveData ? undefined : 'disabled',
    },
    {
      title: 'Gender',
      name: 'gender',
      width: 100,
      hidden: true,
    },
    {
      title: 'Birthday',
      name: 'birthday',
      width: 120,
      formatterComponent: ({ value }) =>
        value
          ? showSensitiveData
            ? `${moment(value).format('DD/MM/YYYY')}`
            : hideSensitiveData(value, 4)
          : '',
      filter: 'disabled',
      sorting: 'disabled',
      hidden: true,
    },
    {
      title: 'Fiscal Code',
      name: 'fiscalCode',
      width: 100,
      formatterComponent: ({ value }) =>
        showSensitiveData ? value : hideSensitiveData(value, 4),
      filter: showSensitiveData ? undefined : 'disabled',
      sorting: showSensitiveData ? undefined : 'disabled',
      hidden: true,
    },
    {
      title: 'City',
      name: 'city',
      width: 80,
      formatterComponent: ({ value }) =>
        showSensitiveData ? value : hideSensitiveData(value, 4),
      filter: showSensitiveData ? undefined : 'disabled',
      sorting: showSensitiveData ? undefined : 'disabled',
      hidden: true,
    },
    {
      title: 'Car Brand',
      name: 'carBrand',
      width: 100,
      hidden: true,
    },
    {
      title: 'Car Model',
      name: 'carModel',
      width: 100,
      hidden: true,
    },
    {
      title: 'Note',
      name: 'note',
      width: 100,
      hidden: true,
    },
    {
      title: 'Status',
      name: 'status',
      width: 100,
      sort: true,
      formatterComponent: ({ row }) =>
        row.qualified === 'new' && row.survey === 'new' ? (
          <span className="badge badge-warning font-size-12">NEW</span>
        ) : row.error === '' || !row.error ? (
          <span className="badge badge-success font-size-12">OK</span>
        ) : (
          <span className="badge badge-danger font-size-12">ERROR</span>
        ),
      filter: (value, filter, row) => getFilter(statusName(row), filter.value),
      sorting: 'disabled',
      exportValue: ({ row }) =>
        row.qualified === 'new' && row.survey === 'new'
          ? 'NEW'
          : row.error === '' || !row.error
          ? 'OK'
          : 'ERROR',
    },
    {
      title: 'Qualified',
      name: 'qualified',
      width: 110,
      formatterComponent: ({ value }) => qualifiedName(value, true),
      filter: (value, filter) => getFilter(qualifiedName(value), filter.value),
      sorting: 'disabled',
    },
    {
      title: 'Marketing Consent',
      name: 'marketingConsent',
      width: 120,
      hidden: true,
    },
    {
      title: 'Third Party Consent',
      name: 'thirdPartyConsent',
      width: 120,
      hidden: true,
    },
    {
      title: 'Occupation',
      name: 'occupation',
      width: 120,
      hidden: true,
    },
    {
      title: 'Source',
      name: 'source',
      width: 100,
      hidden: true,
    },
    {
      title: 'Phone verified',
      name: 'verifiedPhoneNumber',
      width: 100,
      formatterComponent: ({ value }) =>
        value ? (
          <span className="badge badge-success font-size-12">YES</span>
        ) : (
          <span className="badge badge-warning font-size-12">NO</span>
        ),
      filter: (value, filter) => getFilter(value ? 'yes' : 'no', filter.value),
      sorting: (a, b) => getSort(a ? 'yes' : 'no', b ? 'yes' : 'no'),
      hidden: true,
    },
    ...aiColumns,
    {
      title: 'Created At',
      name: 'createdAt',
      width: 120,
      formatterComponent: ({ value }) => <FromNow value={value} />,
      sorting: (a, b) => getSort(moment(a).valueOf(), moment(b).valueOf()),
      filter: (value, filter) =>
        getFilter(`${moment(value).fromNow()}`, filter.value),
      exportValue: ({ value }) => moment(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    actions,
  ];
};

export default createTableColumns;
